import { usersEmptyApi as api } from "./users.empty-api";

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<GetUserApiResponse, GetUserApiArg>({
      query: () => ({ url: `/user` }),
    }),
    getUserSecretKey: build.query<
      GetUserSecretKeyApiResponse,
      GetUserSecretKeyApiArg
    >({
      query: () => ({ url: `/user/secret_key` }),
    }),
    getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
      query: (queryArg) => ({
        url: `/users`,
        params: {
          user_params: queryArg.userParams,
        },
      }),
    }),
    getUsersByUserId: build.query<
      GetUsersByUserIdApiResponse,
      GetUsersByUserIdApiArg
    >({
      query: (queryArg) => ({ url: `/users/${queryArg.userId}` }),
    }),
    deleteUsersByUserId: build.mutation<
      DeleteUsersByUserIdApiResponse,
      DeleteUsersByUserIdApiArg
    >({
      query: (queryArg) => ({
        url: `/users/${queryArg.userId}`,
        method: "DELETE",
      }),
    }),
    getUserSecrets: build.query<
      GetUserSecretsApiResponse,
      GetUserSecretsApiArg
    >({
      query: (queryArg) => ({
        url: `/user/secrets`,
        params: {
          user_secrets_params: queryArg.userSecretsParams,
        },
      }),
    }),
    postUserSecrets: build.mutation<
      PostUserSecretsApiResponse,
      PostUserSecretsApiArg
    >({
      query: (queryArg) => ({
        url: `/user/secrets`,
        method: "POST",
        body: queryArg.secretPost,
      }),
    }),
    getUserSecretsBySecretId: build.query<
      GetUserSecretsBySecretIdApiResponse,
      GetUserSecretsBySecretIdApiArg
    >({
      query: (queryArg) => ({ url: `/user/secrets/${queryArg.secretId}` }),
    }),
    patchUserSecretsBySecretId: build.mutation<
      PatchUserSecretsBySecretIdApiResponse,
      PatchUserSecretsBySecretIdApiArg
    >({
      query: (queryArg) => ({
        url: `/user/secrets/${queryArg.secretId}`,
        method: "PATCH",
        body: queryArg.secretPatch,
      }),
    }),
    deleteUserSecretsBySecretId: build.mutation<
      DeleteUserSecretsBySecretIdApiResponse,
      DeleteUserSecretsBySecretIdApiArg
    >({
      query: (queryArg) => ({
        url: `/user/secrets/${queryArg.secretId}`,
        method: "DELETE",
      }),
    }),
    getUserSshKeys: build.query<
      GetUserSshKeysApiResponse,
      GetUserSshKeysApiArg
    >({
      query: () => ({ url: `/user/ssh_keys` }),
    }),
    postUserSshKeys: build.mutation<
      PostUserSshKeysApiResponse,
      PostUserSshKeysApiArg
    >({
      query: (queryArg) => ({
        url: `/user/ssh_keys`,
        method: "POST",
        body: queryArg.sshKeyPost,
      }),
    }),
    getUserSshKeysByKeyId: build.query<
      GetUserSshKeysByKeyIdApiResponse,
      GetUserSshKeysByKeyIdApiArg
    >({
      query: (queryArg) => ({ url: `/user/ssh_keys/${queryArg.keyId}` }),
    }),
    deleteUserSshKeysByKeyId: build.mutation<
      DeleteUserSshKeysByKeyIdApiResponse,
      DeleteUserSshKeysByKeyIdApiArg
    >({
      query: (queryArg) => ({
        url: `/user/ssh_keys/${queryArg.keyId}`,
        method: "DELETE",
      }),
    }),
    getError: build.query<GetErrorApiResponse, GetErrorApiArg>({
      query: () => ({ url: `/error` }),
    }),
    getVersion: build.query<GetVersionApiResponse, GetVersionApiArg>({
      query: () => ({ url: `/version` }),
    }),
    getUserPreferences: build.query<
      GetUserPreferencesApiResponse,
      GetUserPreferencesApiArg
    >({
      query: () => ({ url: `/user/preferences` }),
    }),
    postUserPreferencesDismissProjectMigrationBanner: build.mutation<
      PostUserPreferencesDismissProjectMigrationBannerApiResponse,
      PostUserPreferencesDismissProjectMigrationBannerApiArg
    >({
      query: () => ({
        url: `/user/preferences/dismiss_project_migration_banner`,
        method: "POST",
      }),
    }),
    deleteUserPreferencesDismissProjectMigrationBanner: build.mutation<
      DeleteUserPreferencesDismissProjectMigrationBannerApiResponse,
      DeleteUserPreferencesDismissProjectMigrationBannerApiArg
    >({
      query: () => ({
        url: `/user/preferences/dismiss_project_migration_banner`,
        method: "DELETE",
      }),
    }),
    postUserPreferencesPinnedProjects: build.mutation<
      PostUserPreferencesPinnedProjectsApiResponse,
      PostUserPreferencesPinnedProjectsApiArg
    >({
      query: (queryArg) => ({
        url: `/user/preferences/pinned_projects`,
        method: "POST",
        body: queryArg.addPinnedProject,
      }),
    }),
    deleteUserPreferencesPinnedProjects: build.mutation<
      DeleteUserPreferencesPinnedProjectsApiResponse,
      DeleteUserPreferencesPinnedProjectsApiArg
    >({
      query: (queryArg) => ({
        url: `/user/preferences/pinned_projects`,
        method: "DELETE",
        params: {
          delete_pinned_params: queryArg.deletePinnedParams,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as usersGeneratedApi };
export type GetUserApiResponse =
  /** status 200 The currently logged in user */ SelfUserInfo;
export type GetUserApiArg = void;
export type GetUserSecretKeyApiResponse =
  /** status 200 The secret key of the currently logged in user.
This endpoint is not publicly accessible.
 */ UserSecretKey;
export type GetUserSecretKeyApiArg = void;
export type GetUsersApiResponse =
  /** status 200 The list of users in the service (this is a subset of what is in Keycloak) */ UsersWithId;
export type GetUsersApiArg = {
  userParams?: {
    /** Return the user(s) with an exact match on the email provided */
    exact_email?: string;
  };
};
export type GetUsersByUserIdApiResponse =
  /** status 200 The requested user */ UserWithId;
export type GetUsersByUserIdApiArg = {
  userId: string;
};
export type DeleteUsersByUserIdApiResponse = unknown;
export type DeleteUsersByUserIdApiArg = {
  userId: string;
};
export type GetUserSecretsApiResponse =
  /** status 200 The user's secrets */ SecretsList;
export type GetUserSecretsApiArg = {
  userSecretsParams?: {
    /** Filter results based on secret kind */
    kind?: SecretKind;
  };
};
export type PostUserSecretsApiResponse =
  /** status 201 Secret successfully created */ SecretWithId;
export type PostUserSecretsApiArg = {
  secretPost: SecretPost;
};
export type GetUserSecretsBySecretIdApiResponse =
  /** status 200 The secret */ SecretWithId;
export type GetUserSecretsBySecretIdApiArg = {
  secretId: Ulid;
};
export type PatchUserSecretsBySecretIdApiResponse =
  /** status 201 Secret successfully changed */ SecretWithId;
export type PatchUserSecretsBySecretIdApiArg = {
  secretId: Ulid;
  secretPatch: SecretPatch;
};
export type DeleteUserSecretsBySecretIdApiResponse = unknown;
export type DeleteUserSecretsBySecretIdApiArg = {
  secretId: Ulid;
};
export type GetUserSshKeysApiResponse =
  /** status 200 The user's SSH public keys */ SshKeysList;
export type GetUserSshKeysApiArg = void;
export type PostUserSshKeysApiResponse =
  /** status 201 SSH public key successfully registered */ SshKey;
export type PostUserSshKeysApiArg = {
  sshKeyPost: SshKeyPost;
};
export type GetUserSshKeysByKeyIdApiResponse =
  /** status 200 The SSH public key */ SshKey;
export type GetUserSshKeysByKeyIdApiArg = {
  keyId: Ulid;
};
export type DeleteUserSshKeysByKeyIdApiResponse = unknown;
export type DeleteUserSshKeysByKeyIdApiArg = {
  keyId: Ulid;
};
export type GetErrorApiResponse = unknown;
export type GetErrorApiArg = void;
export type GetVersionApiResponse = /** status 200 The error */ Version;
export type GetVersionApiArg = void;
export type GetUserPreferencesApiResponse =
  /** status 200 The user preferences */ UserPreferences;
export type GetUserPreferencesApiArg = void;
export type PostUserPreferencesDismissProjectMigrationBannerApiResponse =
  /** status 200 The updated user preferences */ UserPreferences;
export type PostUserPreferencesDismissProjectMigrationBannerApiArg = void;
export type DeleteUserPreferencesDismissProjectMigrationBannerApiResponse =
  /** status 200 The updated user preferences */ UserPreferences;
export type DeleteUserPreferencesDismissProjectMigrationBannerApiArg = void;
export type PostUserPreferencesPinnedProjectsApiResponse =
  /** status 200 The updated user preferences */ UserPreferences;
export type PostUserPreferencesPinnedProjectsApiArg = {
  addPinnedProject: AddPinnedProject;
};
export type DeleteUserPreferencesPinnedProjectsApiResponse =
  /** status 200 The updated user preferences */ UserPreferences;
export type DeleteUserPreferencesPinnedProjectsApiArg = {
  /** query parameters */
  deletePinnedParams?: {
    project_slug?: string;
  };
};
export type UserId = string;
export type Username = string;
export type UserEmail = string;
export type UserFirstLastName = string;
export type SelfUserInfo = {
  id: UserId;
  username: Username;
  email?: UserEmail;
  first_name?: UserFirstLastName;
  last_name?: UserFirstLastName;
  /** Whether the user is a platform administrator or not */
  is_admin: boolean;
};
export type ErrorResponse = {
  error: {
    code: number;
    detail?: string;
    message: string;
    /** Sentry trace ID for linking to corresponding log entries */
    trace_id?: string;
  };
};
export type UserSecretKey = {
  /** The users secret key */
  secret_key?: string;
};
export type UserWithId = {
  id: UserId;
  username: Username;
  email?: UserEmail;
  first_name?: UserFirstLastName;
  last_name?: UserFirstLastName;
};
export type UsersWithId = UserWithId[];
export type Ulid = string;
export type SecretName = string;
export type SecretKind = "general" | "storage";
export type ExpirationTimestamp = string | null;
export type ModificationDate = string;
export type SecretDefaultFilename = string;
export type UlidList = Ulid[];
export type SecretWithId = {
  id: Ulid;
  name: SecretName;
  kind: SecretKind;
  expiration_timestamp?: ExpirationTimestamp;
  modification_date: ModificationDate;
  default_filename: SecretDefaultFilename;
  session_secret_slot_ids: UlidList;
  data_connector_ids: UlidList;
};
export type SecretsList = SecretWithId[];
export type SecretValue = string;
export type SecretPost = {
  name: SecretName;
  value: SecretValue;
  kind?: SecretKind & any;
  expiration_timestamp?: ExpirationTimestamp;
  default_filename?: SecretDefaultFilename;
};
export type SecretPatch = {
  name?: SecretName;
  value?: SecretValue;
  expiration_timestamp?: ExpirationTimestamp;
  default_filename?: SecretDefaultFilename;
};
export type SshKey = {
  id: Ulid;
  public_key: string;
  key_type: string;
  fingerprint: string;
  name?: string | null;
  created_at: string;
};
export type SshKeysList = SshKey[];
export type SshKeyPost = {
  public_key: string;
  name?: string | null;
};
export type Version = {
  version: string;
};
export type ProjectSlugResponse = string;
export type PinnedProjects = {
  project_slugs?: ProjectSlugResponse[];
};
export type ShowProjectMigrationBanner = boolean;
export type UserPreferences = {
  user_id: UserId;
  pinned_projects: PinnedProjects;
  show_project_migration_banner?: ShowProjectMigrationBanner;
};
export type ProjectSlug = string;
export type AddPinnedProject = {
  project_slug: ProjectSlug;
};
export const {
  useGetUserQuery,
  useGetUserSecretKeyQuery,
  useGetUsersQuery,
  useGetUsersByUserIdQuery,
  useDeleteUsersByUserIdMutation,
  useGetUserSecretsQuery,
  usePostUserSecretsMutation,
  useGetUserSecretsBySecretIdQuery,
  usePatchUserSecretsBySecretIdMutation,
  useDeleteUserSecretsBySecretIdMutation,
  useGetUserSshKeysQuery,
  usePostUserSshKeysMutation,
  useGetUserSshKeysByKeyIdQuery,
  useDeleteUserSshKeysByKeyIdMutation,
  useGetErrorQuery,
  useGetVersionQuery,
  useGetUserPreferencesQuery,
  usePostUserPreferencesDismissProjectMigrationBannerMutation,
  useDeleteUserPreferencesDismissProjectMigrationBannerMutation,
  usePostUserPreferencesPinnedProjectsMutation,
  useDeleteUserPreferencesPinnedProjectsMutation,
} = injectedRtkApi;
